import React from "react";
import {
  Briefcase01Icon,
  Building04Icon,
  ChartIncreaseIcon,
  Clock04Icon,
  DashboardCircleIcon,
  EarthIcon,
  JobLinkIcon,
  LinkSquare01Icon,
  Location04Icon,
  Mortarboard01Icon,
  StarIcon,
} from "hugeicons-react";
import Image from "next/image";
import togetherWorkLogo from "@/assets/imgs/Logo-mini.jpg";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";
import { useClientTranslations } from "@/lib/translate";
import { Job } from "@/modules/types/job/job";
import { diffDate } from "@/lib/utils/formatter";
import moment from "moment";
import DisplaySkills from "@/modules/components/commons/DisplaySkills";
import { JobSalaryDisplay } from "./JobSalaryDisplay";
import { useMetadata } from "@/modules/hooks/metadata/useMetadata";
import ListImagesPopupView from "@/modules/components/commons/ListImagesPopupView";
import JobApplyStatusBadge from "./JobApplyStatusBadge";
import AIStar from "../commons/icon/AIStar";
import { useAIGenSuggestionForJob } from "@/modules/hooks/employee/useEmployeeInfo";
import Markdown from "react-markdown";
import { useApplyJob } from "@/modules/hooks/apply/useApplyJob";
import AICallLoginPopup from "./AICallLoginPopup";
import ApplyCallLoginPopup from "./ApplyCallLoginPopup";
import CircularProgress from "../commons/CircularProgress";
import ApplyJobModal from "./ApplyJobModal";
import { SaveJobButton } from "./SaveJobButton";

export const JobDetailsSide = ({ job }: { job: Job }) => {
  const { t } = useClientTranslations("job-info");
  const { t: tMeta } = useClientTranslations("metadata");
  const { industries, levels, categories, degrees } = useMetadata();

  // Tabs state for details/AI
  const [activeTab, setActiveTab] = React.useState("details");
  const {
    genSuggestion,
    loading,
    suggestion,
    openLoginModal,
    onCloseLoginModal,
  } = useAIGenSuggestionForJob(job.id);
  const {
    loading: applyLoading,
    openLoginModal: applyOpenLoginModal,
    onCloseLoginModal: onCloseApplyLoginModal,
    openApplyJobModal,
    setOpenApplyJobModal,
    applyJob,
    onApplyJob,
    applyStatus,
  } = useApplyJob(job.id);

  const handleGenerateSuggestion = async () => {
    await genSuggestion();
  };

  const yearExperienceFormat = (minYear: number, maxYear: number) => {
    if (maxYear <= 0) {
      if (minYear > 0) {
        return `${t("atLeast")} ${minYear} ${minYear > 1 ? t("years") : t("year")
          }`;
      } else {
        return t("noExperience");
      }
    }
    if (minYear === maxYear) {
      return `${minYear} ${minYear > 1 ? t("years") : t("year")}`;
    }
    if (minYear <= 0) {
      if (maxYear === Infinity) {
        return t("anyExperience");
      }
      return `${t("atLeast")} ${maxYear} ${maxYear > 1 ? t("years") : t("year")
        }`;
    }
    return `${minYear} - ${maxYear} ${t("years")}`;
  };

  // Job apply status badge logic
  const jobApplyStatusComponent = () => {
    if (!job.jobApplyStatus && !applyStatus) return null;
    return (
      <JobApplyStatusBadge
        forceUpdate
        jobApplyId={job.jobApplyId || ""}
        status={job.jobApplyStatus || applyStatus!}
      />
    );
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm h-[calc(100vh-120px)] sticky top-[90px] flex flex-col">
      {/* Fixed Header Section */}
      <div className="w-full space-y-6 bg-white border-b border-gray-300 pb-5">
        <div className="flex items-start gap-4">
          <div className="relative w-20 h-20">
            <Image
              src={job.companyInfo?.companyLogo || togetherWorkLogo}
              alt={job.companyInfo?.companyName || "Together Work"}
              width={700}
              height={700}
              className="object-contain rounded-lg"
            />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex gap-2 items-center">
              {job.jobTitle}{" "}
              <Link
                href={`${routes.job.detailPath}${job.slug}`}
                target="_blank"
              >
                <LinkSquare01Icon />
              </Link>
            </h2>
            {job.companyInfo && (
              <p className="text-lg text-gray-600">
                <span className="hidden">{t("fromCompany")}</span>
                {job.companyInfo.companyName}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center text-gray-600">
            <Location04Icon className="w-5 h-5 mr-2" />
            <span>{job.address}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Briefcase01Icon className="w-5 h-5 mr-2" />
            <span>{tMeta(`jobType.${job.jobType}`)}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Clock04Icon className="w-5 h-5 mr-2" />
            {job.createdAt && (
              <span>{diffDate(job.createdAt, moment().unix())}</span>
            )}
          </div>
          <div className="text-primary font-semibold">
            <JobSalaryDisplay
              requiredLogin={job.requiredLogin}
              isNegotiable={job.isNegotiable}
              minSalary={job.minSalary}
              maxSalary={job.maxSalary}
              moneyUnit={job.moneyUnit}
              loginToViewSalary={t("loginToViewSalary")}
              salaryNegotiable={t("salaryNegotiable")}
            />
          </div>
        </div>

        <div className="w-full flex gap-3 items-center">
          <ApplyCallLoginPopup
            applyOpenLoginModal={applyOpenLoginModal}
            onCloseApplyLoginModal={onCloseApplyLoginModal}
          />
          {job.isApplied || applyStatus ? (
            jobApplyStatusComponent()
          ) : (
            <button
              className="w-full bg-primary text-white py-3 max-h-fit rounded-lg hover:bg-primaryDark transition-colors duration-300"
              onClick={applyJob}
            >
              {applyLoading ? <CircularProgress /> : t("applyNow")}
            </button>
          )}
          {/* Chat with Business button after CV screening success */}
          {(job.jobApplyStatus === "accepted" ||
            job.jobApplyStatus === "exchange") && (
              <Link
                // Temporary, we need to add conversation id for this
                href={`${routes.candidate.message}`}
                className="w-full bg-secondary text-white py-3 rounded-lg mt-2 text-center"
              >
                Chat with Business
              </Link>
            )}
          <SaveJobButton
            jobId={job.id}
            isSaved={job.isSaved}
            disabled={job.isApplied}
          />
        </div>
      </div>

      {/* Tabs Header */}
      <div className="flex justify-center space-x-4 my-4">
        <button
          className={`relative px-6 py-2 font-semibold transition-all duration-200 ${activeTab === "details"
            ? "text-primary"
            : "text-gray-500 hover:text-primary"
            }`}
          onClick={() => setActiveTab("details")}
        >
          {t("jobDetails")}
          {activeTab === "details" && (
            <span className="absolute left-0 right-0 -bottom-1 h-1 bg-primary rounded-full"></span>
          )}
        </button>
        <button
          className={`relative px-6 py-2 font-semibold transition-all duration-200 ${activeTab === "ai"
            ? "text-primary"
            : "text-gray-500 hover:text-primary"
            }`}
          onClick={() => setActiveTab("ai")}
        >
          <AIStar className="mr-2" />
          {t("aiGenSuggestion")}
          {activeTab === "ai" && (
            <span className="absolute left-0 right-0 -bottom-1 h-1 bg-primary rounded-full"></span>
          )}
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-3 py-3 custom-scrollbar">
        {activeTab === "details" && (
          <div className="space-y-4">
            {/* Company Images */}
            {job.companyInfo?.companyImages && (
              <ListImagesPopupView images={job.companyInfo.companyImages} />
            )}
            <p className="text-gray-700">
              <span className="font-semibold">{t("skills")}: </span>
            </p>
            <div className="w-full list-inside">
              <DisplaySkills skills={job.skills} />
            </div>
            <p className="flex items-center justify-start gap-1 text-gray-700">
              <JobLinkIcon className="size-5" />
              <span className="font-semibold">{t("experience")}: </span>
              {yearExperienceFormat(
                job.minYearExperience,
                job.maxYearExperience
              )}
            </p>
            <p className="flex items-center justify-start gap-1 text-gray-700">
              <ChartIncreaseIcon className="size-5" />
              <span className="font-semibold">{t("level")}: </span>
              {levels.find((level) => level.id === job.levelId)?.title}
            </p>
            <p className="flex items-center justify-start gap-1 text-gray-700">
              <DashboardCircleIcon className="size-5" />
              <span className="font-semibold">{t("category")}: </span>
              {
                categories.find((category) => category.id === job.categoryId)
                  ?.title
              }
            </p>
            <p className="flex items-center justify-start gap-1 text-gray-700">
              <Mortarboard01Icon className="size-5" />
              <span className="font-semibold">{t("educate")}: </span>
              {degrees.find((degree) => degree.id === job.degreeId)?.title}
            </p>

            <div className="w-full border-t border-gray-300 border-dashed mt-5" />
            <h3 className="text-xl font-semibold text-gray-700">
              {t("jobDescription")}
            </h3>
            <div
              className="text-gray-600 w-full list-inside"
              dangerouslySetInnerHTML={{ __html: job.jobDescription }}
            ></div>

            <h3 className="text-xl font-semibold text-gray-700">
              {t("requirements")}
            </h3>
            <div
              className="text-gray-600 w-full list-inside"
              dangerouslySetInnerHTML={{ __html: job.jobRequirement }}
            ></div>

            {job.jobResponsibility && job.jobResponsibility.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-700">
                  {t("responsibilities")}
                </h3>
                <div
                  className="text-gray-600 w-full list-inside"
                  dangerouslySetInnerHTML={{ __html: job.jobResponsibility }}
                ></div>
              </>
            )}

            <h3 className="text-xl font-semibold text-gray-700">
              {t("benefits")}
            </h3>
            <div
              className="text-gray-600 w-full list-inside"
              dangerouslySetInnerHTML={{ __html: job.jobBenefit }}
            ></div>

            {job.jobNote && job.jobNote.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-gray-700">
                  {t("note")}
                </h3>
                <div
                  className="text-gray-600 w-full list-inside"
                  dangerouslySetInnerHTML={{ __html: job.jobNote }}
                ></div>
              </>
            )}

            {job.companyInfo && (
              <>
                <div className="w-full border-t border-gray-300 border-dashed mt-5" />
                <h3 className="text-xl font-semibold text-gray-700">
                  {t("companyInfo")}
                </h3>
                <div className="text-gray-800 w-full list-inside">
                  <div className="w-full space-y-2">
                    <h4 className="text-lg flex gap-2 w-full font-semibold text-gray-800">
                      {job.companyInfo.companyName}{" "}
                      <Link
                        href={
                          routes.company.detailPath + "/" + job.companyInfo.slug
                        }
                        title="Xem thêm thông tin công ty"
                      >
                        <LinkSquare01Icon />
                      </Link>
                    </h4>
                    <p className="text-gray-800 italic whitespace-pre-line">
                      {job.companyInfo.companySlogan}
                    </p>
                    <div className="mt-2">
                      {job.companyInfo.companyDescription && (
                        <div
                          className="text-gray-600 w-full list-inside"
                          dangerouslySetInnerHTML={{
                            __html: job.companyInfo.companyDescription,
                          }}
                        ></div>
                      )}
                    </div>
                  </div>
                  <div className="w-full grid grid-cols-2 gap-4 mt-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Building04Icon className="w-5 h-5 text-gray-500" />
                        <span>
                          {
                            industries.find(
                              (industry) =>
                                industry.id === job.companyInfo?.industryId
                            )?.title
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <EarthIcon className="w-5 h-5 text-gray-500" />
                        <a
                          href={job.companyInfo.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {job.companyInfo.website}
                        </a>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Briefcase01Icon className="w-5 h-5 text-gray-500" />
                        <span>
                          {tMeta(`companySize.${job.companyInfo.companySize}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <StarIcon className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                        <span className="flex items-center">
                          {job.companyInfo.starRating.toFixed(1)}
                        </span>
                        <span className="text-gray-500">
                          ({job.companyInfo.reviewCount} {t("reviews")})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {activeTab === "ai" && (
          <div className="ai-tab-content">
            <AICallLoginPopup
              openLoginModal={openLoginModal}
              onCloseLoginModal={onCloseLoginModal}
            />
            {suggestion && suggestion.length > 0 ? (
              <div className="w-full p-6 text-gray-700">
                <Markdown>{suggestion}</Markdown>
              </div>
            ) : (
              <div className="w-full flex flex-col items-center justify-center min-h-[300px]">
                <button
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primaryDark transition-colors duration-300 mb-6 disabled:opacity-60"
                  onClick={handleGenerateSuggestion}
                  disabled={loading}
                >
                  <AIStar className="mr-2" />
                  {loading ? t("genAiButtonLoading") : t("genAiButton")}
                </button>
                <div className="w-full text-center text-gray-700 min-h-[60px]">
                  {loading ? (
                    <div className="w-full p-6 flex flex-col gap-2">
                      <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[70%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[80%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[70%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[80%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[70%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                    </div>
                  ) : (
                    <p>{t("genAiButtonDisabled")}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <ApplyJobModal
        open={openApplyJobModal}
        onClose={() => setOpenApplyJobModal(false)}
        onSubmit={onApplyJob}
        loading={applyLoading}
      />
    </div>
  );
};
// ⭐
JobDetailsSide.displayName = "JobDetailsSide";
